import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { db, auth } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, AppEmptyState, Badge, IconBox } from "../components/app";
import { appColors, appType } from "../utils/appTheme";
import { formatDateLocalized } from "../utils/formatters";
import { FaRegComments, FaPhoneAlt } from "react-icons/fa";

// Consultas y llamadas que el residente dejó con Habi (colección habi_consultas):
// acá ve el estado y la respuesta de la administración. Se crean desde el chat
// (dejar_consulta_administracion / agendar_llamada_administracion).

const fmtFecha = (ts) => formatDateLocalized(ts, { month: "short" });

const badgeTone = (status) =>
  status === "resuelta" ? "green" : status === "respondida" ? "blue" : "amber";

export default function MisConsultas({ goBack }) {
  const { t } = useTranslation("misConsultas");
  const { selectedBuilding } = useBuilding();
  // readOnly solo es true en "Ver como residente" (EffectiveIdentityProvider).
  const { email: effEmail, readOnly: viendoComo } = useEffectiveIdentity();
  const isMobile = useIsMobile();
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedBuilding?.buildingid) return undefined;
    let unsub = () => {};
    let cancelado = false;

    (async () => {
      if (!effEmail) { setLoading(false); return; }
      if (cancelado) return;
      const bid = selectedBuilding.buildingid;
      const variants = tolerantId(bid).map(String);
      const vecino = effEmail.toLowerCase();
      let q;
      if (viendoComo) {
        // "Ver como residente": la sesión es del staff, no del vecino. Las
        // reglas le dejan listar solo SU bandeja (adminEmails con su email)
        // en un edificio donde es staff: la misma consulta que ConsultasAdmin,
        // y se muestran las de este vecino.
        const staffEmail = (auth.currentUser?.email || "").toLowerCase();
        if (!staffEmail) { setConsultas([]); setLoading(false); return; }
        q = query(
          collection(db, "habi_consultas"),
          where("adminEmails", "array-contains", staffEmail),
          where("buildingid", "in", tolerantId(bid))
        );
      } else {
        // Query por residentEmail: es lo único que las reglas dejan listar a
        // un residente (sus propias consultas). El edificio se filtra en cliente.
        q = query(collection(db, "habi_consultas"), where("residentEmail", "==", vecino));
      }
      unsub = onSnapshot(
        q,
        (snap) => {
          const rows = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((c) => variants.includes(String(c.buildingid)))
            .filter((c) => !viendoComo || String(c.residentEmail || "").toLowerCase() === vecino);
          rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setConsultas(rows);
          setLoading(false);
        },
        (err) => { console.error("MisConsultas listener:", err); setLoading(false); }
      );
    })().catch((e) => { console.error("MisConsultas:", e); setLoading(false); });

    return () => { cancelado = true; unsub(); };
  }, [selectedBuilding?.buildingid, effEmail, viendoComo]);

  // En la vista previa el staff no ve todo lo del vecino: solo lo que llegó a su bandeja.
  const avisoVistaPrevia = viendoComo ? (
    <p
      style={{
        ...appType.caption,
        margin: "0 0 12px",
        padding: 10,
        background: appColors.surface2,
        borderRadius: 10,
      }}
    >
      {t("previewNote")}
    </p>
  ) : null;

  const lista = loading ? (
    <p style={{ ...appType.bodySm, textAlign: "center", padding: 40 }}>{t("loading")}</p>
  ) : consultas.length === 0 ? (
    <AppEmptyState
      icon={<FaRegComments />}
      title={t("empty.title")}
      description={t("empty.description")}
    />
  ) : (
    consultas.map((c) => {
      const esLlamada = c.type === "llamada";
      return (
        <AppCard key={c.id} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <IconBox
              icon={esLlamada ? <FaPhoneAlt /> : <FaRegComments />}
              color={esLlamada ? appColors.amber : appColors.blue}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={appType.tileName}>{esLlamada ? t("type.call") : t("type.query")}</span>
                <Badge tone={badgeTone(c.status || "pendiente")}>
                  {t(`status.${c.status}`, { defaultValue: t("status.pendiente") })}
                </Badge>
                <span style={{ ...appType.caption, marginLeft: "auto" }}>{fmtFecha(c.createdAt)}</span>
              </div>
              {esLlamada && c.call && (
                <p style={{ ...appType.bodySm, color: appColors.amber, margin: "6px 0 0" }}>
                  📞 {c.call.label || `${c.call.date} ${c.call.time}`}
                </p>
              )}
              <p style={{ ...appType.body, margin: "6px 0 0", whiteSpace: "pre-wrap" }}>{c.message}</p>
              {c.adminResponse ? (
                <div style={{ marginTop: 10, padding: 10, background: appColors.surface2, borderRadius: 10 }}>
                  <span style={appType.caption}>{t("adminResponseLabel")}</span>
                  <p style={{ ...appType.bodySm, margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{c.adminResponse}</p>
                </div>
              ) : (
                <p style={{ ...appType.caption, margin: "8px 0 0" }}>
                  {t("noResponseYet")}
                </p>
              )}
            </div>
          </div>
        </AppCard>
      );
    })
  );

  if (isMobile) {
    return (
      <AppScreen
        header={
          <AppHeader
            title={t("header.title")}
            subtitle={selectedBuilding?.name ? t("header.subtitleWithBuilding", { name: selectedBuilding.name }) : t("header.subtitle")}
            onBack={goBack}
          />
        }
      >
        {avisoVistaPrevia}
        {lista}
      </AppScreen>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
      <h1 style={{ ...appType.screenTitle, margin: 0 }}>{t("header.title")}</h1>
      <p style={{ ...appType.bodySm, margin: "4px 0 20px" }}>
        {t("desktopSubtitle")}
      </p>
      {avisoVistaPrevia}
      {lista}
    </div>
  );
}
